import { useMemo, useRef, useState } from "react";
import { Modal } from "@/components/base/Modal";
import { parseCsv, toCsv, downloadCsv } from "@/lib/csv";
import type { ImportSummary, ParsedRow, RowAction } from "@/lib/imports";
import { friendlyError } from "@/lib/errors";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  requiredColumns: string[];
  optionalColumns?: string[];
  templateHeaders: string[];
  templateRows: string[][];
  onParse: (rows: string[][], headers: string[]) => Promise<ParsedRow[]>;
  onExecute: (rows: ParsedRow[]) => Promise<ImportSummary>;
  onDone: () => void;
}

type Stage = "select" | "parsing" | "preview" | "importing" | "results";

export default function StagedImportModal({
  open, onClose, title, description, requiredColumns, optionalColumns, templateHeaders, templateRows, onParse, onExecute, onDone,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>("select");
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const reset = () => {
    setStage("select");
    setRows([]);
    setError("");
    setSummary(null);
    setProgress(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    const text = await file.text();
    const csv = parseCsv(text);
    if (csv.length < 2) {
      setError("No data rows found in the CSV file.");
      e.target.value = "";
      return;
    }
    const headers = csv[0].map((h) => h.trim().toLowerCase());
    const missing = requiredColumns.filter((c) => !headers.includes(c));
    if (missing.length > 0) {
      setError(`The CSV is missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}.`);
      e.target.value = "";
      return;
    }
    setStage("parsing");
    try {
      const parsed = await onParse(csv, headers);
      setRows(parsed);
      setStage("preview");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to validate the file.");
      setStage("select");
    }
    e.target.value = "";
  };

  const changeAction = (idx: number, action: RowAction) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, action } : r)));
  };

  const canImport = useMemo(() => {
    const importable = rows.filter((r) => r.errors.length === 0 && r.action !== "skip");
    return importable.length > 0;
  }, [rows]);

  const counts = useMemo(() => {
    const total = rows.length;
    const errors = rows.filter((r) => r.errors.length > 0).length;
    const warnings = rows.filter((r) => r.warnings.length > 0).length;
    const conflicts = rows.filter((r) => r.conflicts.length > 0).length;
    const create = rows.filter((r) => r.action === "create" && r.errors.length === 0).length;
    const update = rows.filter((r) => r.action === "update" && r.errors.length === 0).length;
    const skip = rows.filter((r) => r.action === "skip").length;
    return { total, errors, warnings, conflicts, create, update, skip };
  }, [rows]);

  const runImport = async () => {
    setStage("importing");
    setProgress({ done: 0, total: 0 });
    try {
      const result = await onExecute(rows);
      setSummary(result);
      setStage("results");
      if (result.created > 0 || result.updated > 0) onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed.");
      setStage("preview");
    }
  };

  const downloadTemplate = () => {
    downloadCsv(`import-template.csv`, toCsv(templateHeaders, templateRows));
  };

  const downloadResults = () => {
    if (!summary) return;
    const headers = ["row", "result", "detail"];
    const body: (string | number)[][] = [];
    summary.failed.forEach((f) => body.push([f.label, "failed", f.error]));
    summary.needsAttention.forEach((n) => body.push([n.label, "needs attention", n.error]));
    if (body.length === 0) body.push(["—", "no issues", ""]);
    downloadCsv(`import-results-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, body));
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      wide
      footer={
        stage === "preview" ? (
          <>
            <button onClick={close} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={runImport}
              disabled={!canImport}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Import {counts.create + counts.update} record{(counts.create + counts.update) === 1 ? "" : "s"}
            </button>
          </>
        ) : stage === "results" ? (
          <>
            <button onClick={downloadResults} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
              <i className="ri-download-2-line" /> Download results
            </button>
            <button onClick={close} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap">Done</button>
          </>
        ) : (
          <button onClick={close} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Close</button>
        )
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed">{description}</p>

        {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

        {stage === "select" && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Required columns: <span className="font-mono">{requiredColumns.join(", ")}</span>
              {optionalColumns && optionalColumns.length > 0 && (
                <> · Optional: <span className="font-mono">{optionalColumns.join(", ")}</span></>
              )}
            </p>
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              <button onClick={() => fileRef.current?.click()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                <i className="ri-upload-2-line" /> Choose CSV file
              </button>
              <button onClick={downloadTemplate} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                <i className="ri-download-2-line" /> Download template
              </button>
            </div>
            <p className="text-xs text-slate-400">Nothing is imported until you review the preview and confirm.</p>
          </div>
        )}

        {stage === "parsing" && (
          <div className="py-6 text-center text-sm text-slate-500">Validating your file…</div>
        )}

        {stage === "preview" && (
          <>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full">{counts.total} rows</span>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full">{counts.create} create</span>
              <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">{counts.update} update</span>
              <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">{counts.skip} skip</span>
              {counts.conflicts > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">{counts.conflicts} conflict</span>}
              {counts.warnings > 0 && <span className="text-xs bg-amber-50 text-amber-600 px-2.5 py-1 rounded-full">{counts.warnings} warnings</span>}
              {counts.errors > 0 && <span className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full">{counts.errors} errors</span>}
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Row</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Record</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="px-3 py-2 text-xs text-slate-400 font-mono whitespace-nowrap">{r.sourceLine}</td>
                        <td className="px-3 py-2 text-sm text-slate-800">{r.label}</td>
                        <td className="px-3 py-2">
                          {r.errors.length > 0 ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Error</span>
                          ) : r.conflicts.length > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">Conflict</span>
                              <select
                                value={r.action}
                                onChange={(e) => changeAction(i, e.target.value as RowAction)}
                                className="text-xs px-2 py-1 bg-slate-50 border border-slate-200 rounded-md"
                              >
                                <option value="skip">Skip</option>
                                <option value="create">Create new</option>
                              </select>
                            </div>
                          ) : r.updateId ? (
                            <select
                              value={r.action}
                              onChange={(e) => changeAction(i, e.target.value as RowAction)}
                              className="text-xs px-2 py-1 bg-slate-50 border border-slate-200 rounded-md"
                            >
                              <option value="update">Update</option>
                              <option value="create">Create new</option>
                              <option value="skip">Skip</option>
                            </select>
                          ) : (
                            <select
                              value={r.action}
                              onChange={(e) => changeAction(i, e.target.value as RowAction)}
                              className="text-xs px-2 py-1 bg-slate-50 border border-slate-200 rounded-md"
                            >
                              <option value="create">Create</option>
                              <option value="skip">Skip</option>
                            </select>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {r.matchNote && <span className="text-amber-600 block">{r.matchNote}</span>}
                          {r.conflicts.map((c, j) => <span key={j} className="text-amber-600 block font-medium">{c}</span>)}
                          {r.warnings.map((w, j) => <span key={j} className="text-amber-600 block">{w}</span>)}
                          {r.errors.map((er, j) => <span key={j} className="text-red-600 block">{er}</span>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {stage === "importing" && (
          <div className="py-8 text-center">
            <div className="inline-block h-6 w-6 rounded-full border-2 border-slate-200 border-t-emerald-600 animate-spin" />
            <p className="text-sm text-slate-500 mt-3">
              {progress && progress.total > 0 ? `Importing ${progress.done} of ${progress.total}…` : "Importing…"}
            </p>
          </div>
        )}

        {stage === "results" && summary && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-emerald-700">{summary.created}</p>
                <p className="text-xs text-emerald-600 font-medium mt-1">Created</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-amber-700">{summary.updated}</p>
                <p className="text-xs text-amber-600 font-medium mt-1">Updated</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-slate-700">{summary.skipped}</p>
                <p className="text-xs text-slate-500 font-medium mt-1">Skipped</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${summary.partial > 0 ? "bg-amber-50" : "bg-slate-50"}`}>
                <p className={`text-2xl font-bold ${summary.partial > 0 ? "text-amber-700" : "text-slate-700"}`}>{summary.partial}</p>
                <p className={`text-xs font-medium mt-1 ${summary.partial > 0 ? "text-amber-600" : "text-slate-500"}`}>Needs attention</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${summary.failed.length ? "bg-red-50" : "bg-slate-50"}`}>
                <p className={`text-2xl font-bold ${summary.failed.length ? "text-red-700" : "text-slate-700"}`}>{summary.failed.length}</p>
                <p className={`text-xs font-medium mt-1 ${summary.failed.length ? "text-red-600" : "text-slate-500"}`}>Failed</p>
              </div>
            </div>
            {summary.needsAttention.length > 0 && (
              <div className="border border-amber-100 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-xs font-semibold text-amber-700">Partially imported — needs attention</div>
                <div className="max-h-48 overflow-y-auto divide-y divide-amber-50">
                  {summary.needsAttention.map((n, i) => (
                    <div key={i} className="px-3 py-2 text-sm">
                      <span className="font-medium text-slate-800">{n.label}</span>
                      <span className="text-amber-600 block text-xs mt-0.5">{friendlyError(n.error)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {summary.failed.length > 0 && (
              <div className="border border-red-100 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-xs font-semibold text-red-700">Failed rows</div>
                <div className="max-h-48 overflow-y-auto divide-y divide-red-50">
                  {summary.failed.map((f, i) => (
                    <div key={i} className="px-3 py-2 text-sm">
                      <span className="font-medium text-slate-800">{f.label}</span>
                      <span className="text-red-600 block text-xs mt-0.5">{friendlyError(f.error)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}